import type { CSSProperties } from "react";
import { APEX_LOGO_SIZES, APEX_LOGO_VARIANTS, APEX_GREEN_LOGO_VARIANTS, APEX_GREEN_DARK_LOGO_VARIANTS, type ApexLogoSize } from "@/lib/site-assets";

function pickVariant(target: number): ApexLogoSize {
  for (const s of APEX_LOGO_SIZES) {
    if (s >= target) return s;
  }
  return APEX_LOGO_SIZES[APEX_LOGO_SIZES.length - 1];
}

export interface ApexLogoProps {
  size: number;
  tone?: "original" | "green";
  className?: string;
  style?: CSSProperties;
  alt?: string;
}

/**
 * Official APEX brand mark, served from R2 under site-assets/apex/.
 * Picks the nearest hosted variant (16/32/64/128/256) and emits a
 * 2x srcSet so retina displays load a sharper variant.
 */
export function ApexLogo({ size, tone = "original", className, style, alt = "APEX" }: ApexLogoProps) {
  const oneX = pickVariant(size);
  const twoX = pickVariant(size * 2);
  const variants = tone === "green" ? APEX_GREEN_LOGO_VARIANTS : APEX_LOGO_VARIANTS;
  const src = variants[oneX];
  const srcSet = oneX === twoX ? undefined : `${src} 1x, ${variants[twoX]} 2x`;
  const darkSrc = APEX_GREEN_DARK_LOGO_VARIANTS[oneX];
  const darkSrcSet = oneX === twoX ? undefined : `${darkSrc} 1x, ${APEX_GREEN_DARK_LOGO_VARIANTS[twoX]} 2x`;
  if (tone === "green") return (
    <>
      <img src={src} srcSet={srcSet} width={size} height={size} alt={alt} className={`${className ?? ""} dark:hidden`} style={style} decoding="async" loading="lazy" />
      <img src={darkSrc} srcSet={darkSrcSet} width={size} height={size} alt={alt} className={`${className ?? ""} hidden dark:inline-block`} style={style} decoding="async" loading="lazy" />
    </>
  );
  return (
    <img
      src={src}
      srcSet={srcSet}
      width={size}
      height={size}
      alt={alt}
      className={className}
      style={style}
      decoding="async"
      loading="lazy"
    />
  );
}
